import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  ClipboardList, Plus, BookOpen, LayoutTemplate, Dumbbell, Wind, ShieldCheck, Heart,
  Users, User, CheckCircle, Clock, AlertCircle, TrendingUp, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const CAT_COLORS = {
  STRENGTH: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  AEROBIC: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  RTP: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  RECOVERY: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  MIXED: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
};
const CAT_ICONS = { STRENGTH: Dumbbell, AEROBIC: Wind, RTP: ShieldCheck, RECOVERY: Heart, MIXED: ClipboardList };
const TYPE_LABELS = { SINGLE_SESSION: 'Session', MICROCYCLE: 'Microcycle' };
const TARGET_ICONS = { ATHLETE: User, GROUP: Users, TEAM: Users };

export default function PrescriptionHomePage() {
  const { api, hasRole } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const canEdit = hasRole(['ADMIN', 'SNC', 'PHYSIO']);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, rxRes] = await Promise.all([
        api.get('/prescriptions/dashboard/stats'),
        api.get('/prescriptions', {
          params: {
            ...(catFilter !== 'ALL' ? { category: catFilter } : {}),
            ...(typeFilter !== 'ALL' ? { prescription_type: typeFilter } : {}),
          }
        }),
      ]);
      setStats(statsRes.data);
      setPrescriptions(rxRes.data);
    } catch (e) {
      toast.error('Failed to load prescriptions');
    } finally {
      setLoading(false);
    }
  }, [api, catFilter, typeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const statCards = stats ? [
    { label: 'Active Prescriptions', value: stats.active_prescriptions, icon: ClipboardList, color: 'text-amber-400' },
    { label: 'Completion Rate', value: `${stats.completion_rate}%`, icon: TrendingUp, color: 'text-emerald-400' },
    { label: 'Assigned', value: stats.assigned, icon: Clock, color: 'text-blue-400' },
    { label: 'Completed', value: stats.completed, icon: CheckCircle, color: 'text-emerald-400' },
  ] : [];

  return (
    <div className="space-y-6" data-testid="prescription-home-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100" data-testid="prescriptions-title">Prescriptions</h1>
          <p className="text-sm text-zinc-500 mt-1">Create, assign, and monitor training prescriptions</p>
        </div>
        {canEdit && (
          <Button onClick={() => navigate('/staff/prescriptions/builder')} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="new-prescription-btn">
            <Plus className="w-4 h-4 mr-2" /> New Prescription
          </Button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="prescription-stats">
          {statCards.map((s, i) => (
            <Card key={i} className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-zinc-800/50 rounded-sm">
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                </div>
                <div>
                  <div className="text-xl font-bold text-zinc-100">{s.value}</div>
                  <div className="text-xs text-zinc-500">{s.label}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Quick links */}
      <div className="flex gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate('/staff/prescriptions/library')} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="goto-library-btn">
          <BookOpen className="w-4 h-4 mr-2" /> Exercise Library
          {stats && <Badge className="ml-2 bg-zinc-800 text-zinc-400 text-[10px]">{stats.exercises}</Badge>}
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate('/staff/prescriptions/templates')} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="goto-templates-btn">
          <LayoutTemplate className="w-4 h-4 mr-2" /> Templates
          {stats && <Badge className="ml-2 bg-zinc-800 text-zinc-400 text-[10px]">{stats.templates}</Badge>}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-full sm:w-[160px] bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="category-filter">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="ALL" className="text-zinc-100">All Categories</SelectItem>
            {['STRENGTH','AEROBIC','RTP','RECOVERY','MIXED'].map(c => (
              <SelectItem key={c} value={c} className="text-zinc-100">{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-[160px] bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="type-filter">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="ALL" className="text-zinc-100">All Types</SelectItem>
            <SelectItem value="SINGLE_SESSION" className="text-zinc-100">Single Session</SelectItem>
            <SelectItem value="MICROCYCLE" className="text-zinc-100">Microcycle</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Prescription List */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="text-zinc-500">Loading...</div></div>
      ) : prescriptions.length === 0 ? (
        <Card className="bg-zinc-900/30 border-zinc-800 border-dashed">
          <CardContent className="p-12 text-center">
            <ClipboardList className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <h3 className="text-zinc-400 font-medium mb-1">No prescriptions yet</h3>
            <p className="text-sm text-zinc-600 mb-4">{canEdit ? 'Create your first training prescription' : 'No prescriptions assigned yet'}</p>
            {canEdit && (
              <Button onClick={() => navigate('/staff/prescriptions/builder')} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="create-first-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Prescription
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="prescriptions-list">
          {prescriptions.map(rx => {
            const Icon = CAT_ICONS[rx.category] || ClipboardList;
            const TargetIcon = TARGET_ICONS[rx.target_type] || User;
            return (
              <Card
                key={rx.id}
                className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer"
                onClick={() => navigate(`/staff/prescriptions/${rx.id}`)}
                data-testid={`prescription-card-${rx.id}`}
              >
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-sm ${CAT_COLORS[rx.category]}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-zinc-100 text-sm">{rx.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className={`text-[10px] ${CAT_COLORS[rx.category]}`}>{rx.category}</Badge>
                        <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-500">{TYPE_LABELS[rx.prescription_type] || rx.prescription_type}</Badge>
                        {rx.session_id && <Badge variant="outline" className="text-[10px] border-indigo-500/30 text-indigo-400">Planner</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-xs text-zinc-400">
                        <TargetIcon className="w-3.5 h-3.5" />
                        <span>{rx.target_type}</span>
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        {rx.completed_count}/{rx.assignment_count} done
                      </div>
                    </div>
                    {rx.team_code && <Badge className="bg-zinc-800 text-zinc-400 text-xs">{rx.team_code}</Badge>}
                    <ChevronRight className="w-4 h-4 text-zinc-600" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
